import { useToast } from '../../hooks/useToast';
import type { FilterModification } from '../ai-search/adaptAiSearchFiltersForPage';

const TOAST_ID = 'ai-search-filter-compatibility';

/**
 * The AI can resolve far more selections than a page's cap ("the top 20 tech
 * companies" drops 14), so the list is truncated — an auto-dismissing toast
 * that turns into a wall of text is worse than the generic message it replaces.
 */
const MAX_DROPPED_NAMES_SHOWN = 3;

/** Renders the dropped selections, or `undefined` when there are none to name. */
const summarizeDropped = (droppedNames: string[]): string | undefined => {
  // AI-resolved companies carry their numeric RCID as `selection.name` until a
  // later API round patches in the real label (see resolveUnknownCompanyNames);
  // this toast fires BEFORE that patch, so a raw id here would read "dropped
  // 1435892 and 2 more". Drop id-only names and fall through to the generic
  // message when nothing nameable survives — a bare number is never a company
  // name a user would recognise.
  const nameable = droppedNames.filter((name) => !/^\d+$/.test(name.trim()));
  if (nameable.length === 0) {
    return undefined;
  }
  if (nameable.length <= MAX_DROPPED_NAMES_SHOWN) {
    return nameable.join(', ');
  }
  const shown = nameable.slice(0, MAX_DROPPED_NAMES_SHOWN).join(', ');
  return `${shown} and ${nameable.length - MAX_DROPPED_NAMES_SHOWN} more`;
};

export const useAiSearchCompatibilityToast = () => {
  const toast = useToast();

  const showCompatibilityToast = (modifications: FilterModification[]) => {
    if (modifications.length === 0 || toast.isActive(TOAST_ID)) return;

    const messages: string[] = [];

    const condensed = modifications.filter((m) => m.type === 'condensed_to_or');
    const removed = modifications.filter(
      (m) => m.type === 'removed_not_filter'
    );
    const limited = modifications.filter(
      (m) => m.type === 'limited_primary_selections'
    );
    const limitedInstances = modifications.filter(
      (m) => m.type === 'limited_filter_instances'
    );
    const normalizedGranularity = modifications.filter(
      (m) => m.type === 'normalized_filter_granularity'
    );

    const uniqueNames = (items: FilterModification[]) =>
      Array.from(new Set(items.map((item) => item.filterName)));

    if (condensed.length > 0) {
      messages.push(
        `Combined multiple ${uniqueNames(condensed).join(
          ', '
        )} filters so they work together on this page`
      );
    }
    if (removed.length > 0) {
      messages.push(
        `Removed ${uniqueNames(removed).join(
          ', '
        )} exclusions because this page doesn't support them`
      );
    }
    if (limited.length > 0) {
      const names = uniqueNames(limited).join(', ');
      // Naming the casualties matters most here: the cap deactivates chips
      // rather than removing them, so a dropped company is otherwise invisible.
      const droppedNames = Array.from(
        new Set(limited.flatMap((item) => item.droppedNames ?? []))
      );
      const limits = new Set(
        limited.flatMap((item) =>
          item.limit === undefined ? [] : [item.limit]
        )
      );
      const onlyLimit = limits.size === 1 ? [...limits][0] : undefined;
      const droppedSummary = summarizeDropped(droppedNames);

      if (droppedSummary === undefined) {
        messages.push(
          `This page limits ${names} selections, so we kept the first ones`
        );
      } else if (onlyLimit !== undefined) {
        messages.push(
          `This page supports ${onlyLimit} ${names} selections — kept the first ${onlyLimit} and dropped ${droppedSummary}`
        );
      } else {
        messages.push(
          `This page limits ${names} selections, so we kept the first ones and dropped ${droppedSummary}`
        );
      }
    }
    if (limitedInstances.length > 0) {
      const names = uniqueNames(limitedInstances);
      if (names.length === 1) {
        messages.push(
          `This page supports one ${names[0]} filter, so we kept the first one`
        );
      } else {
        messages.push(
          `This page supports one filter per ${names.join(
            ', '
          )}, so we kept the first ones`
        );
      }
    }
    if (normalizedGranularity.length > 0) {
      messages.push(
        `Standardized ${uniqueNames(normalizedGranularity).join(
          ', '
        )} selections to a single granularity level because this page requires it`
      );
    }

    toast({
      title: 'We adjusted a few filters',
      description: messages.join('. '),
      status: 'info',
      variant: 'subtle',
      duration: 6000,
      isClosable: true,
      position: 'top-right',
      id: TOAST_ID,
    });
  };

  return showCompatibilityToast;
};
